/**
 * Sensitive Data Categories
 *
 * Reference data for sensitive data types, labels, and per-record cost multipliers.
 * Used by SensitiveDataProfile (Impact Calibration) and loss calculation engine.
 *
 * Per-record costs based on IBM/Ponemon Cost of a Data Breach Report
 * and industry regulatory fine benchmarks.
 */

export const SENSITIVE_DATA_CATEGORIES = {
  pii: {
    key: 'pii',
    label: 'Personally Identifiable Information (PII)',
    shortLabel: 'PII',
    costPerRecord: 165,
    description: 'Names, SSNs, addresses, dates of birth, and other personal identifiers',
    regulatoryContext: 'State privacy laws, CCPA/CPRA, GDPR'
  },
  phi: {
    key: 'phi',
    label: 'Protected Health Information (PHI)',
    shortLabel: 'PHI',
    costPerRecord: 408,
    description: 'Medical records, diagnoses, treatment information, health insurance data',
    regulatoryContext: 'HIPAA, HITECH Act'
  },
  pci: {
    key: 'pci',
    label: 'Payment Card Data (PCI)',
    shortLabel: 'PCI',
    costPerRecord: 180,
    description: 'Credit/debit card numbers, CVVs, cardholder data',
    regulatoryContext: 'PCI DSS'
  },
  financial: {
    key: 'financial',
    label: 'Financial Records',
    shortLabel: 'Financial',
    costPerRecord: 195,
    description: 'Bank account numbers, financial statements, tax records, loan data',
    regulatoryContext: 'GLBA, FFIEC, SOX'
  },
  ip_trade_secrets: {
    key: 'ip_trade_secrets',
    label: 'Intellectual Property / Trade Secrets',
    shortLabel: 'IP/Trade Secrets',
    costPerRecord: 250,
    description: 'Proprietary formulas, source code, business strategies, R&D data',
    regulatoryContext: 'Trade secret law, NDAs'
  },
  student_ferpa: {
    key: 'student_ferpa',
    label: 'Student Records (FERPA)',
    shortLabel: 'Student/FERPA',
    costPerRecord: 155,
    description: 'Student educational records, grades, enrollment data, financial aid',
    regulatoryContext: 'FERPA'
  },
  government_cui: {
    key: 'government_cui',
    label: 'Government / Controlled Unclassified Information (CUI)',
    shortLabel: 'Gov/CUI',
    costPerRecord: 220,
    description: 'Government contract data, CUI markings, federal information',
    regulatoryContext: 'CMMC, NIST 800-171, DFARS'
  }
}

// Ordered array for display
export const SENSITIVE_DATA_CATEGORY_LIST = Object.values(SENSITIVE_DATA_CATEGORIES)

/**
 * Map business scoping data type keys to sensitive data category keys.
 * Business scoping dt-1 uses different keys than our category system.
 */
export const BUSINESS_SCOPING_TO_CATEGORY_MAP = {
  'pii': 'pii',
  'phi': 'phi',
  'pci': 'pci',
  'financial-data': 'financial',
  'financial': 'financial',
  'ip-trade-secrets': 'ip_trade_secrets',
  'ip_trade_secrets': 'ip_trade_secrets',
  'student-ferpa': 'student_ferpa',
  'student_ferpa': 'student_ferpa',
  'ferpa': 'student_ferpa',
  'government-cui': 'government_cui',
  'government_cui': 'government_cui',
  'cui': 'government_cui',
  // Additional mappings for common business scoping data type values
  'donor-data': 'pii',
  'client-confidential': 'pii'
}

/**
 * Extract applicable sensitive data categories from business scoping responses.
 * @param {Object} businessScoping - assessment_responses.business_scoping
 * @returns {Object[]} Array of { category, source: 'business_scoping' }
 */
export function extractCategoriesFromBusinessScoping(businessScoping) {
  if (!businessScoping) return []

  // dt-1 holds the selected data types
  const dt1 = businessScoping['dt-1']
  const dataTypes = Array.isArray(dt1)
    ? dt1
    : (dt1?.value || [])

  const seen = new Set()
  const categories = []

  for (const dt of dataTypes) {
    const categoryKey = BUSINESS_SCOPING_TO_CATEGORY_MAP[dt]
    if (categoryKey && !seen.has(categoryKey)) {
      seen.add(categoryKey)
      const category = SENSITIVE_DATA_CATEGORIES[categoryKey]
      if (category) {
        categories.push({
          ...category,
          source: 'business_scoping'
        })
      }
    }
  }

  return categories
}

/**
 * Calculate per-category breach costs from volume data.
 * @param {Object[]} volumes - Array of { data_category, estimated_record_count }
 * @returns {{ totalCost: number, breakdown: Object[], hasData: boolean }}
 */
export function calculateCategoryBreachCosts(volumes) {
  if (!volumes || volumes.length === 0) {
    return { totalCost: 0, breakdown: [], hasData: false }
  }

  const breakdown = []
  let totalCost = 0

  for (const vol of volumes) {
    const category = SENSITIVE_DATA_CATEGORIES[vol.data_category]
    if (!category || !vol.estimated_record_count) continue

    const recordCount = vol.estimated_record_count
    const costPerRecord = category.costPerRecord

    // Apply logarithmic scaling for large record counts (same pattern as existing impactCalibration)
    const effectiveRecords = Math.min(recordCount, 100000) +
      (recordCount > 100000
        ? Math.log10(recordCount - 100000) * 15000
        : 0)

    const cost = Math.round(effectiveRecords * costPerRecord)
    totalCost += cost

    breakdown.push({
      categoryKey: vol.data_category,
      label: category.shortLabel,
      fullLabel: category.label,
      recordCount,
      effectiveRecords: Math.round(effectiveRecords),
      costPerRecord,
      totalCost: cost
    })
  }

  return {
    totalCost,
    breakdown: breakdown.sort((a, b) => b.totalCost - a.totalCost),
    hasData: breakdown.length > 0
  }
}
